import React, { useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { useInView } from 'react-intersection-observer';
import { FiX } from 'react-icons/fi';
import SafeIcon from '../common/SafeIcon';

const Gallery = () => {
  const [selectedImage, setSelectedImage] = useState(null);
  const [ref, inView] = useInView({
    threshold: 0.2,
    triggerOnce: true
  });

  const images = [
    { id: 1, category: 'Villa', title: 'The Planter\'s Suite', emoji: '🏔️' },
    { id: 2, category: 'Nature', title: 'Sinharaja Mist', emoji: '🌿' },
    { id: 3, category: 'Heritage', title: 'Maduwanwela Walawwa', emoji: '🏛️' },
    { id: 4, category: 'Adventure', title: 'Waulpane Caves', emoji: '🦇' },
    { id: 5, category: 'Nature', title: 'Blue Magpie Sighting', emoji: '🐦' },
    { id: 6, category: 'Villa', title: 'Rainforest Wing', emoji: '🌺' },
    { id: 7, category: 'Adventure', title: 'Hidden Waterfall', emoji: '💧' },
    { id: 8, category: 'Heritage', title: 'Traditional Cuisine', emoji: '🍛' }
  ];

  const categories = ['All', 'Villa', 'Nature', 'Heritage', 'Adventure'];
  const [activeCategory, setActiveCategory] = useState('All');

  const filteredImages = activeCategory === 'All' 
    ? images 
    : images.filter(img => img.category === activeCategory);

  const containerVariants = {
    hidden: { opacity: 0 },
    visible: {
      opacity: 1,
      transition: {
        staggerChildren: 0.1
      }
    }
  };

  const itemVariants = {
    hidden: { opacity: 0, scale: 0.8 },
    visible: {
      opacity: 1,
      scale: 1,
      transition: { duration: 0.5 }
    }
  };

  return (
    <section id="gallery" className="py-20 bg-gradient-to-br from-mist-grey to-forest-green/10 relative overflow-hidden">
      {/* Animated background */}
      <div className="absolute inset-0">
        <motion.div
          animate={{
            rotate: [0, 360],
          }}
          transition={{
            duration: 60,
            repeat: Infinity,
            ease: 'linear'
          }}
          className="absolute top-1/4 left-1/4 w-96 h-96 rounded-full bg-gradient-to-r from-terracotta-earth/5 to-forest-green/5"
        />
        <motion.div
          animate={{
            rotate: [360, 0],
          }}
          transition={{
            duration: 45,
            repeat: Infinity,
            ease: 'linear'
          }}
          className="absolute bottom-1/4 right-1/4 w-64 h-64 rounded-full bg-gradient-to-l from-forest-green/5 to-terracotta-earth/5"
        />
      </div>

      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 relative z-10">
        <motion.div
          ref={ref}
          variants={containerVariants}
          initial="hidden"
          animate={inView ? "visible" : "hidden"}
          className="text-center mb-16"
        >
          <motion.h2 
            variants={itemVariants}
            className="text-4xl md:text-5xl font-playfair font-bold text-forest-green mb-6"
          >
            Visual Journey
          </motion.h2>
          
          <motion.div 
            variants={itemVariants}
            className="w-24 h-1 bg-terracotta-earth mx-auto mb-8"
          ></motion.div>
          
          <motion.p 
            variants={itemVariants}
            className="text-xl font-lato text-gray-700 max-w-3xl mx-auto leading-relaxed"
          >
            Glimpses of the sanctuary that awaits. Each image tells the story 
            of nature's grandeur and cultural richness.
          </motion.p>
        </motion.div>

        {/* Category Filter */}
        <motion.div
          variants={containerVariants}
          className="flex flex-wrap justify-center gap-4 mb-12"
        >
          {categories.map((category) => (
            <motion.button
              key={category}
              variants={itemVariants}
              whileHover={{ scale: 1.05 }}
              whileTap={{ scale: 0.95 }}
              onClick={() => setActiveCategory(category)}
              className={`px-6 py-3 rounded-full font-lato font-medium transition-all duration-300 ${
                activeCategory === category
                  ? 'bg-forest-green text-white shadow-lg'
                  : 'glass-button text-forest-green hover:bg-forest-green/10'
              }`}
            >
              {category}
            </motion.button>
          ))}
        </motion.div>

        {/* Gallery Grid */}
        <motion.div
          layout
          className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-6"
        >
          <AnimatePresence>
            {filteredImages.map((image) => (
              <motion.div
                key={image.id}
                layout
                variants={itemVariants}
                initial="hidden"
                animate="visible"
                exit="hidden"
                whileHover={{ scale: 1.05, y: -5 }}
                onClick={() => setSelectedImage(image)}
                className="glass-card p-6 cursor-pointer group aspect-square flex flex-col items-center justify-center"
              >
                <div className="text-4xl md:text-5xl mb-4 group-hover:scale-110 transition-transform duration-300">
                  {image.emoji}
                </div>
                <h3 className="text-sm md:text-base font-playfair font-semibold text-forest-green text-center">
                  {image.title}
                </h3>
                <span className="text-xs font-lato text-gray-600 mt-1">
                  {image.category}
                </span>
              </motion.div>
            ))}
          </AnimatePresence>
        </motion.div>
      </div>

      {/* Modal */}
      <AnimatePresence>
        {selectedImage && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            className="fixed inset-0 bg-black/80 backdrop-blur-sm z-50 flex items-center justify-center p-4"
            onClick={() => setSelectedImage(null)}
          >
            <motion.div
              initial={{ scale: 0.5, opacity: 0 }}
              animate={{ scale: 1, opacity: 1 }}
              exit={{ scale: 0.5, opacity: 0 }}
              className="glass-card p-8 max-w-2xl w-full text-center relative"
              onClick={(e) => e.stopPropagation()}
            >
              <button
                onClick={() => setSelectedImage(null)}
                className="absolute top-4 right-4 text-white hover:text-terracotta-earth transition-colors"
              >
                <SafeIcon icon={FiX} className="w-6 h-6" />
              </button>

              <div className="text-8xl mb-6">{selectedImage.emoji}</div>
              <h3 className="text-2xl font-playfair font-bold text-white mb-4">
                {selectedImage.title}
              </h3>
              <p className="font-lato text-white/80">
                Professional photography showcasing the beauty and essence of {selectedImage.title.toLowerCase()}. 
                Experience the authentic atmosphere that makes Thalagaha Estate unique.
              </p>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>
    </section>
  );
};

export default Gallery;